import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  FaCalendarAlt,
  FaCheck,
  FaEdit,
  FaLayerGroup,
  FaLock,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import {
  createBonusInstallments,
  deleteBonusInstallment,
  updateBonusInstallment,
} from "../commissionsApi";
import { formatCurrency } from "./commissionDetailConstants";
import {
  INSTALLMENT_FREQUENCY_OPTIONS,
  buildInstallmentBatchCreatePayload,
  generateBonusInstallments,
  resolveInstallmentGroupSplit,
  toInputDate,
} from "./commissionInstallmentUtils";
import {
  analyzeBudgetAllocation,
  splitInstallmentsByLock,
} from "./tuitionFeeSyncUtils";

const controlClass =
  "h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-[12px] font-medium text-slate-800 outline-none transition focus:border-amber-500 focus:ring-1 focus:ring-amber-500/20 disabled:bg-slate-50";

const amountControlClass = `${controlClass} tabular-nums`;

function sanitizeMoneyInput(value) {
  if (value === "" || value === null || value === undefined) return "";
  let cleaned = String(value).replace(/[^\d.]/g, "");
  const dotIndex = cleaned.indexOf(".");
  if (dotIndex !== -1) {
    cleaned =
      cleaned.slice(0, dotIndex + 1) +
      cleaned.slice(dotIndex + 1).replace(/\./g, "");
  }
  return cleaned;
}

function Chip({ children, tone = "slate" }) {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-600",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${tones[tone] || tones.slate}`}
    >
      {children}
    </span>
  );
}

function statusTone(status) {
  const value = String(status || "pending").toLowerCase();
  if (value === "paid") return "green";
  if (value === "overdue") return "amber";
  return "slate";
}

function isPendingInstallment(row) {
  const status = String(row?.status || "pending").toLowerCase();
  return status === "pending";
}

function CreateBonusInstallmentModal({
  open,
  onClose,
  plan,
  bonus,
  currency,
  onSaved,
  mode = "create",
  bonusBudget = null,
  nextInstallmentNo = 1,
}) {
  const isAppend = mode === "append";
  const planBonus = Math.max(0, Number(bonus?.expectedBonus) || 0);
  const effectiveBudget = Math.max(
    0,
    Number(bonusBudget != null ? bonusBudget : planBonus) || 0,
  );
  const [frequency, setFrequency] = useState(isAppend ? "custom" : "monthly");
  const [count, setCount] = useState(isAppend ? "1" : "4");
  const [startDate, setStartDate] = useState(
    toInputDate(new Date().toISOString()),
  );
  const [preview, setPreview] = useState([]);
  const [groupAmount, setGroupAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const lastSplitBaseRef = useRef(null);

  const { splitBase, userSpecifiedAmount } = useMemo(
    () => resolveInstallmentGroupSplit(groupAmount, effectiveBudget),
    [groupAmount, effectiveBudget],
  );

  useEffect(() => {
    if (!open) return;
    setFrequency(isAppend ? "custom" : "monthly");
    setCount(isAppend ? "1" : "4");
    setStartDate(toInputDate(new Date().toISOString()));
    setGroupAmount("");
    setPreview([]);
    lastSplitBaseRef.current = null;
    setSaving(false);
    setFormError("");
  }, [open, isAppend]);

  useEffect(() => {
    if (!open || !bonus?.id) {
      setPreview([]);
      return;
    }
    if (effectiveBudget <= 0) {
      setPreview([]);
      lastSplitBaseRef.current = null;
      return;
    }
    const preserveRowAmounts = lastSplitBaseRef.current === splitBase;
    setPreview((prev) => {
      const rows = generateBonusInstallments({
        tuitionFee: plan?.tuitionFee,
        bonusType: bonus.bonusType,
        bonusValue: bonus.bonusValue,
        gstType: bonus.gstType,
        expectedBonus: splitBase,
        count: Number(count) || 1,
        frequency,
        startDate,
      });

      return rows.map((row, index) => {
        const previous = prev[index];
        const bonusAmount =
          preserveRowAmounts &&
          previous?.bonus_amount != null &&
          previous?.bonus_amount !== ""
            ? previous.bonus_amount
            : row.bonus_amount;
        return {
          ...row,
          installment_no: Number(nextInstallmentNo) + index,
          due_date:
            frequency === "custom"
              ? index === 0
                ? toInputDate(startDate)
                : previous?.due_date || ""
              : row.due_date,
          bonus_amount: bonusAmount,
          remarks: previous?.remarks ?? row.remarks,
        };
      });
    });
    lastSplitBaseRef.current = splitBase;
  }, [
    open,
    plan,
    bonus,
    count,
    frequency,
    startDate,
    splitBase,
    effectiveBudget,
    nextInstallmentNo,
  ]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, saving]);

  const updatePreviewDueDate = (index, value) => {
    setPreview((prev) =>
      prev.map((row, i) => (i === index ? { ...row, due_date: value } : row)),
    );
  };

  const updatePreviewField = (index, field, value) => {
    setPreview((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
  };

  const updatePreviewMoney = (index, rawValue) => {
    const cleaned = sanitizeMoneyInput(rawValue);
    setPreview((prev) => {
      const othersSum = prev.reduce(
        (sum, row, i) =>
          i === index ? sum : sum + (Number(row.bonus_amount) || 0),
        0,
      );
      const batchCap = splitBase > 0 ? splitBase : effectiveBudget;
      const maxForRow =
        Math.round(Math.max(0, batchCap - othersSum) * 100) / 100;
      let amount = cleaned === "" ? "" : Number(cleaned);
      if (amount !== "" && Number.isFinite(amount) && amount > maxForRow) {
        amount = maxForRow;
      }

      return prev.map((row, i) => {
        if (i !== index) return row;
        const bonusAmount =
          amount === "" ? "" : Math.round((Number(amount) || 0) * 100) / 100;
        return {
          ...row,
          bonus_amount: bonusAmount,
        };
      });
    });
  };

  const canCreatePreview =
    effectiveBudget > 0 &&
    preview.length > 0 &&
    preview.every((row) => (Number(row.bonus_amount) || 0) > 0) &&
    (frequency !== "custom" ||
      preview.every((row) => Boolean(toInputDate(row.due_date))));

  const allocatedBonus = useMemo(
    () =>
      preview.reduce((sum, row) => sum + (Number(row.bonus_amount) || 0), 0),
    [preview],
  );

  const handleCreate = async () => {
    if (!bonus?.id || !canCreatePreview) return;
    setFormError("");
    if (preview.some((row) => !(Number(row.bonus_amount) > 0))) {
      setFormError("Enter a bonus amount for each installment.");
      return;
    }
    if (frequency === "custom" && !canCreatePreview) {
      setFormError("Please set a due date for every custom installment.");
      return;
    }
    const groupCap = userSpecifiedAmount ?? effectiveBudget;
    if (allocatedBonus > groupCap + 0.009) {
      setFormError(
        `Bonus total ${formatCurrency(allocatedBonus, currency)} exceeds this group amount ${formatCurrency(groupCap, currency)}.`,
      );
      return;
    }
    if (allocatedBonus > effectiveBudget + 0.009) {
      setFormError(
        `Bonus total ${formatCurrency(allocatedBonus, currency)} exceeds available ${formatCurrency(effectiveBudget, currency)}.`,
      );
      return;
    }
    if (effectiveBudget <= 0) {
      setFormError(
        "There is no remaining bonus amount to create installments for.",
      );
      return;
    }
    setSaving(true);
    try {
      await createBonusInstallments(
        bonus.id,
        buildInstallmentBatchCreatePayload({
          frequency,
          count,
          previewLength: preview.length,
          startDate,
          userSpecifiedAmount,
          installments: preview.map((row) => ({
            installment_no: row.installment_no,
            due_date: row.due_date,
            bonus_amount: Number(row.bonus_amount) || 0,
            gst_type: row.gst_type || bonus.gstType || "none",
            status: row.status || "pending",
            remarks: row.remarks || null,
          })),
        }),
      );
      Swal.fire({
        icon: "success",
        title: isAppend ? "Installments added" : "Installments created",
        text: isAppend
          ? "Additional installments saved for remaining bonus."
          : "Bonus installment plan saved.",
        timer: 1400,
        showConfirmButton: false,
      });
      onClose();
      if (typeof onSaved === "function") onSaved();
    } catch (error) {
      setFormError(error?.message || "Failed to create bonus installments.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <>
      <div
        className="cms-app-modal-backdrop bg-black/40"
        onClick={saving ? undefined : onClose}
        aria-hidden="true"
      />
      <div
        className="cms-app-modal cms-app-modal--centered flex items-center justify-center p-3 sm:p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bonus-installment-modal-title"
        onClick={saving ? undefined : onClose}
      >
        <div
          className="tailwind-container relative z-10 flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div className="flex items-center gap-2.5 border-b border-slate-100 bg-gradient-to-r from-[#FFFBF5] to-white px-4 py-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500 text-white">
              <FaLayerGroup size={13} />
            </div>
            <div className="min-w-0 flex-1">
              <h3
                id="bonus-installment-modal-title"
                className="mb-0 text-[15px] font-semibold text-slate-900"
              >
                {isAppend
                  ? "Add bonus installment"
                  : "Create bonus installment plan"}
              </h3>
              <p className="mb-0 text-[11px] text-slate-400">
                {isAppend
                  ? "Add installments using remaining bonus only"
                  : "Enter an amount for this group, set frequency and count, save — repeat with another amount and frequency for the rest"}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-50"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <label className="col-span-2 block min-w-0 sm:col-span-4">
                <span className="mb-1 block text-[10px] font-semibold uppercase text-slate-400">
                  Amount for these installments (optional)
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  className={amountControlClass}
                  placeholder={`Max ${formatCurrency(effectiveBudget, currency)}`}
                  value={groupAmount}
                  disabled={saving || effectiveBudget <= 0}
                  onChange={(event) => {
                    setFormError("");
                    const cleaned = sanitizeMoneyInput(event.target.value);
                    if (cleaned === "") {
                      setGroupAmount("");
                      return;
                    }
                    const parsed = Number(cleaned);
                    if (!Number.isFinite(parsed)) return;
                    const capped = Math.min(
                      Math.round(parsed * 100) / 100,
                      effectiveBudget,
                    );
                    setGroupAmount(String(capped));
                  }}
                />
                <p className="mb-0 mt-1 text-[10px] text-slate-500">
                  Leave blank to use full available{" "}
                  <strong>{formatCurrency(effectiveBudget, currency)}</strong>
                </p>
              </label>
              <label className="block min-w-0">
                <span className="mb-1 block text-[10px] font-semibold uppercase text-slate-400">
                  Frequency
                </span>
                <select
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                  className={controlClass}
                  disabled={saving}
                >
                  {INSTALLMENT_FREQUENCY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block min-w-0">
                <span className="mb-1 block text-[10px] font-semibold uppercase text-slate-400">
                  Count
                </span>
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={count}
                  onChange={(e) => setCount(e.target.value)}
                  className={controlClass}
                  disabled={saving}
                />
              </label>
              <label className="col-span-2 block min-w-0">
                <span className="mb-1 block text-[10px] font-semibold uppercase text-slate-400">
                  {frequency === "custom" ? "First due date" : "Start date"}
                </span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={controlClass}
                  disabled={saving}
                />
              </label>
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 text-[11px] text-amber-800">
              Example: enter 1,000 with monthly, save, then add 5,000 with
              another frequency. Count splits your group amount across rows;
              edit row amounts if needed.
            </div>

            <div className="flex flex-wrap gap-3 text-[11px] text-slate-500">
              <span>
                This group{" "}
                <strong className="text-amber-700">
                  {formatCurrency(allocatedBonus, currency)}
                </strong>
                {splitBase > 0 ? (
                  <>
                    {" / "}
                    {formatCurrency(splitBase, currency)}
                  </>
                ) : null}
              </span>
              <span>
                Left on bonus after save{" "}
                <strong className="text-emerald-700">
                  {formatCurrency(
                    Math.max(0, effectiveBudget - allocatedBonus),
                    currency,
                  )}
                </strong>
              </span>
            </div>

            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="min-w-full border-collapse text-left text-[12px]">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="px-2.5 py-2 font-semibold">#</th>
                    <th className="px-2.5 py-2 font-semibold">Due date</th>
                    <th className="px-2.5 py-2 font-semibold">Bonus</th>
                    <th className="px-2.5 py-2 font-semibold">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((row, rowIndex) => (
                    <tr
                      key={`bp-${row.installment_no}`}
                      className="border-t border-slate-100"
                    >
                      <td className="px-2.5 py-2 font-semibold text-slate-700">
                        {row.installment_no}
                      </td>
                      <td className="px-2.5 py-2">
                        {frequency === "custom" ? (
                          <input
                            type="date"
                            className={controlClass}
                            value={row.due_date || ""}
                            onChange={(e) =>
                              updatePreviewDueDate(rowIndex, e.target.value)
                            }
                          />
                        ) : (
                          <span className="inline-flex items-center gap-1 text-slate-700">
                            <FaCalendarAlt
                              size={9}
                              className="text-slate-400"
                            />
                            {row.due_date}
                          </span>
                        )}
                      </td>
                      <td className="px-2.5 py-2 tabular-nums font-semibold text-amber-700">
                        <input
                          type="text"
                          inputMode="decimal"
                          className={amountControlClass}
                          placeholder="0.00"
                          value={
                            row.bonus_amount === "" ||
                            row.bonus_amount == null
                              ? ""
                              : String(row.bonus_amount)
                          }
                          disabled={saving}
                          onChange={(e) =>
                            updatePreviewMoney(rowIndex, e.target.value)
                          }
                        />
                      </td>
                      <td className="min-w-[8rem] px-2.5 py-2">
                        <input
                          type="text"
                          className={controlClass}
                          value={row.remarks || ""}
                          placeholder="Remarks"
                          disabled={saving}
                          onChange={(e) =>
                            updatePreviewField(
                              rowIndex,
                              "remarks",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {formError ? (
            <div className="border-t border-rose-100 bg-rose-50 px-4 py-2.5">
              <p className="mb-0 text-[12px] font-medium text-rose-700">
                {formError}
              </p>
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/80 px-4 py-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-[12px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={
                saving || !canCreatePreview || effectiveBudget <= 0
              }
              className={`inline-flex h-9 items-center gap-1.5 rounded-lg px-3.5 text-[12px] font-semibold text-white ${
                saving || !canCreatePreview || effectiveBudget <= 0
                  ? "cursor-not-allowed bg-amber-400/50"
                  : "bg-amber-600 hover:bg-amber-700"
              }`}
              style={{ border: "none" }}
            >
              <FaCheck size={11} />
              {saving
                ? "Saving…"
                : isAppend
                  ? "Add installments"
                  : "Save bonus installments"}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

function EditBonusInstallmentModal({
  open,
  row,
  bonus,
  currency,
  otherBonusTotal = 0,
  onClose,
  onSaved,
}) {
  const planBonus = Math.max(0, Number(bonus?.expectedBonus) || 0);
  const maxBonus = Math.max(
    0,
    Math.round((planBonus - Number(otherBonusTotal || 0)) * 100) / 100,
  );

  const [dueDate, setDueDate] = useState("");
  const [bonusAmount, setBonusAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!open || !row) return;
    setDueDate(toInputDate(row.dueDate) || "");
    setBonusAmount(
      row.bonusAmount != null && row.bonusAmount !== ""
        ? String(row.bonusAmount)
        : "",
    );
    setRemarks(row.remarks || "");
    setSaving(false);
    setFormError("");
  }, [open, row]);

  if (!open || !row) return null;

  const amount = Math.round((Number(bonusAmount) || 0) * 100) / 100;
  const remaining = Math.max(0, Math.round((maxBonus - amount) * 100) / 100);

  const handleSave = async () => {
    setFormError("");
    if (!dueDate) {
      setFormError("Due date is required.");
      return;
    }
    if (amount < 0) {
      setFormError("Bonus amount cannot be negative.");
      return;
    }
    if (amount > maxBonus + 0.009) {
      setFormError(
        `Bonus ${formatCurrency(amount, currency)} exceeds available ${formatCurrency(maxBonus, currency)} for this installment. Reduce the amount.`,
      );
      return;
    }

    setSaving(true);
    try {
      await updateBonusInstallment(row.id, {
        due_date: dueDate,
        bonus_amount: amount,
        gst_type: bonus?.gstType || "none",
        remarks: remarks || null,
      });
      Swal.fire({
        icon: "success",
        title: "Bonus installment updated",
        timer: 1200,
        showConfirmButton: false,
      });
      onClose?.();
      onSaved?.();
    } catch (error) {
      setFormError(error?.message || "Failed to update bonus installment.");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <>
      <div
        className="cms-app-modal-backdrop bg-black/40"
        onClick={saving ? undefined : onClose}
        aria-hidden="true"
      />
      <div
        className="cms-app-modal cms-app-modal--centered flex items-center justify-center p-3 sm:p-4"
        role="dialog"
        aria-modal="true"
        onClick={saving ? undefined : onClose}
      >
        <div
          className="tailwind-container relative z-10 flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div className="flex items-center gap-2.5 border-b border-slate-100 bg-gradient-to-r from-[#FFFBF5] to-white px-4 py-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500 text-white">
              <FaEdit size={13} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="mb-0 text-[15px] font-semibold text-slate-900">
                Edit bonus installment #{row.installmentNo}
              </h3>
              <p className="mb-0 text-[11px] text-slate-400">
                Adjust due date and bonus amount.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-50"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
            <div className="flex flex-wrap gap-3 text-[11px] text-slate-500">
              <span>
                Bonus{" "}
                <strong className="text-amber-700">
                  {formatCurrency(amount, currency)}
                </strong>
                {" / "}
                {formatCurrency(maxBonus, currency)}
              </span>
              <span>
                Remaining{" "}
                <strong className="text-emerald-700">
                  {formatCurrency(remaining, currency)}
                </strong>
              </span>
            </div>

            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="min-w-full border-collapse text-left text-[12px]">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="px-2.5 py-2 font-semibold">#</th>
                    <th className="px-2.5 py-2 font-semibold">Due date</th>
                    <th className="px-2.5 py-2 font-semibold">Bonus</th>
                    <th className="px-2.5 py-2 font-semibold">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-slate-100">
                    <td className="px-2.5 py-2 font-semibold text-slate-700">
                      {row.installmentNo}
                    </td>
                    <td className="px-2.5 py-2">
                      <input
                        type="date"
                        className={controlClass}
                        value={dueDate}
                        disabled={saving}
                        onChange={(e) => {
                          setDueDate(e.target.value);
                          setFormError("");
                        }}
                      />
                    </td>
                    <td className="px-2.5 py-2 tabular-nums">
                      <input
                        type="text"
                        inputMode="decimal"
                        className={amountControlClass}
                        value={bonusAmount}
                        disabled={saving}
                        onChange={(e) => {
                          setBonusAmount(sanitizeMoneyInput(e.target.value));
                          setFormError("");
                        }}
                      />
                    </td>
                    <td className="min-w-[8rem] px-2.5 py-2">
                      <input
                        type="text"
                        className={controlClass}
                        value={remarks}
                        placeholder="Remarks"
                        disabled={saving}
                        onChange={(e) => setRemarks(e.target.value)}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {formError ? (
            <div className="border-t border-rose-100 bg-rose-50 px-4 py-2.5">
              <p className="mb-0 text-[12px] font-medium text-rose-700">
                {formError}
              </p>
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/80 px-4 py-3">
            <button
              type="button"
              disabled={saving}
              onClick={onClose}
              className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-[12px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={handleSave}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-amber-600 px-3.5 text-[12px] font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
              style={{ border: "none" }}
            >
              <FaCheck size={11} />
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

function BonusInstallmentsCard({
  plan,
  bonus,
  currency = "AUD",
  clientId = null,
  returnTo = "",
  onSaved,
}) {
  const hasBonus = Boolean(bonus?.id);
  const savedRows = useMemo(
    () => (Array.isArray(bonus?.installments) ? bonus.installments : []),
    [bonus?.installments],
  );
  const hasSaved = savedRows.length > 0;

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editRow, setEditRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedInstallmentIds, setSelectedInstallmentIds] = useState([]);

  const allocatedBonus = useMemo(
    () =>
      savedRows.reduce((sum, row) => sum + (Number(row.bonusAmount) || 0), 0),
    [savedRows],
  );

  const planBonus = Number(bonus?.expectedBonus) || 0;
  const remainingBonus = Math.max(
    0,
    Math.round((planBonus - allocatedBonus) * 100) / 100,
  );
  const allocation = analyzeBudgetAllocation(allocatedBonus, planBonus);
  const { locked: lockedRows } = splitInstallmentsByLock(savedRows);
  const canAddInstallment = hasSaved && remainingBonus > 0.009;
  const nextInstallmentNo =
    savedRows.reduce(
      (max, row) => Math.max(max, Number(row.installmentNo) || 0),
      0,
    ) + 1;

  const openCreateModal = () => {
    setModalMode("create");
    setModalOpen(true);
  };

  const openAddModal = () => {
    if (!canAddInstallment) {
      Swal.fire(
        "No remaining bonus",
        "All bonus amount is already allocated to installments.",
        "info",
      );
      return;
    }
    setModalMode("append");
    setModalOpen(true);
  };

  const deletableInstallmentIds = useMemo(
    () =>
      savedRows
        .filter((row) => isPendingInstallment(row))
        .map((row) => String(row.id)),
    [savedRows],
  );

  useEffect(() => {
    setSelectedInstallmentIds((prev) =>
      prev.filter((id) => deletableInstallmentIds.includes(String(id))),
    );
  }, [deletableInstallmentIds]);

  const toggleInstallmentSelection = (row) => {
    if (!isPendingInstallment(row)) return;
    const id = String(row.id);
    setSelectedInstallmentIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const toggleSelectAllDeletableInstallments = () => {
    if (selectedInstallmentIds.length === deletableInstallmentIds.length) {
      setSelectedInstallmentIds([]);
      return;
    }
    setSelectedInstallmentIds([...deletableInstallmentIds]);
  };

  const handleDeleteInstallment = async (row) => {
    const installmentId = row?.id;
    if (!installmentId || !isPendingInstallment(row)) return;
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete installment?",
      text: `Remove bonus installment #${row.installmentNo || ""}? This cannot be undone.`,
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });
    if (!confirm.isConfirmed) return;

    setSaving(true);
    try {
      // POST /api/v1/new-commission-bonus-installments/{id}/delete
      await deleteBonusInstallment(installmentId);
      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Bonus installment removed.",
        timer: 1400,
        showConfirmButton: false,
      });
      if (typeof onSaved === "function") onSaved();
    } catch (error) {
      Swal.fire(
        "Error",
        error?.message || "Failed to delete bonus installment.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSelectedInstallments = async () => {
    const rows = savedRows.filter(
      (row) =>
        selectedInstallmentIds.includes(String(row.id)) &&
        isPendingInstallment(row),
    );
    if (!rows.length) return;

    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete selected installments?",
      text: `Remove ${rows.length} bonus installment(s)? This cannot be undone.`,
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });
    if (!confirm.isConfirmed) return;

    setSaving(true);
    try {
      for (const row of rows) {
        await deleteBonusInstallment(row.id);
      }
      setSelectedInstallmentIds([]);
      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: `${rows.length} bonus installment(s) removed.`,
        timer: 1400,
        showConfirmButton: false,
      });
      if (typeof onSaved === "function") onSaved();
    } catch (error) {
      Swal.fire(
        "Error",
        error?.message || "Failed to delete bonus installment(s).",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const allDeletableSelected =
    deletableInstallmentIds.length > 0 &&
    selectedInstallmentIds.length === deletableInstallmentIds.length;

  return (
    <>
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-slate-100 bg-gradient-to-r from-[#FFFBF5] to-white px-3 py-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-amber-500 text-white">
            <FaLayerGroup size={11} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <h3 className="mb-0 text-[13px] font-semibold text-slate-900">
                Bonus installments
              </h3>
              {hasSaved ? (
                <Chip tone="green">{savedRows.length} saved</Chip>
              ) : hasBonus ? (
                <Chip tone="amber">No plan yet</Chip>
              ) : (
                <Chip tone="amber">Needs bonus</Chip>
              )}
              {canAddInstallment ? (
                <Chip tone="amber">
                  Remaining {formatCurrency(remainingBonus, currency)}
                </Chip>
              ) : null}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {selectedInstallmentIds.length > 0 ? (
              <button
                type="button"
                onClick={handleDeleteSelectedInstallments}
                disabled={saving}
                className="inline-flex h-7 items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 text-[10px] font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60"
                style={{ border: "none" }}
              >
                <FaTrash size={9} />
                Delete selected ({selectedInstallmentIds.length})
              </button>
            ) : null}
            {canAddInstallment ? (
              <button
                type="button"
                onClick={openAddModal}
                disabled={saving}
                className="inline-flex h-7 items-center gap-1 rounded-md bg-amber-600 px-2 text-[10px] font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
                style={{ border: "none" }}
              >
                <FaPlus size={9} />
                Add installment
              </button>
            ) : null}
          </div>
        </div>

        <div className="p-2.5">
          {!hasBonus ? (
            <div className="flex flex-col items-center justify-center py-5 text-center">
              <FaLock className="mb-1.5 text-slate-300" size={14} />
              <p className="mb-0 text-[12px] font-semibold text-slate-700">
                Create bonus plan first
              </p>
            </div>
          ) : !hasSaved ? (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-dashed border-amber-200 bg-amber-50/40 px-3 py-3">
              <p className="mb-0 text-[12px] text-slate-500">
                No bonus installment plan yet
              </p>
              <button
                type="button"
                onClick={openCreateModal}
                className="inline-flex h-8 items-center gap-1.5 rounded-md bg-amber-600 px-2.5 text-[11px] font-semibold text-white hover:bg-amber-700"
                style={{ border: "none" }}
              >
                <FaPlus size={10} />
                Create plan
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {allocation.isOver ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
                  <p className="mb-1 text-[12px] font-semibold text-amber-900">
                    Bonus installments exceed expected bonus by{" "}
                    {formatCurrency(allocation.overBy, currency)}
                  </p>
                  <p className="mb-0 text-[11px] text-amber-800/90">
                    Edit pending installments to lower amounts, or delete
                    individual pending rows
                    {lockedRows.length > 0
                      ? `. ${lockedRows.length} invoiced installment(s) are locked.`
                      : "."}
                  </p>
                </div>
              ) : null}
              <div className="flex flex-wrap gap-3 text-[11px] text-slate-500">
                <span>
                  Bonus{" "}
                  <strong className="text-amber-700">
                    {formatCurrency(allocatedBonus, currency)}
                  </strong>
                  {" / "}
                  {formatCurrency(planBonus, currency)}
                </span>
                <span>
                  Remaining{" "}
                  <strong className="text-emerald-700">
                    {formatCurrency(remainingBonus, currency)}
                  </strong>
                </span>
              </div>

              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-full border-collapse text-left text-[12px]">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400">
                    <tr>
                      <th className="w-8 px-2 py-2">
                        {deletableInstallmentIds.length > 0 ? (
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 rounded border-slate-300"
                            checked={allDeletableSelected}
                            disabled={saving}
                            aria-label="Select all deletable bonus installments"
                            onChange={toggleSelectAllDeletableInstallments}
                          />
                        ) : null}
                      </th>
                      <th className="px-2.5 py-2 font-semibold">#</th>
                      <th className="px-2.5 py-2 font-semibold">Due date</th>
                      <th className="px-2.5 py-2 font-semibold">Bonus</th>
                      <th className="px-2.5 py-2 font-semibold">
                        Bonus Received
                      </th>
                      <th className="px-2.5 py-2 font-semibold">Status</th>
                      <th className="px-2.5 py-2 font-semibold">Invoice</th>
                      <th className="px-2.5 py-2 font-semibold text-right">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {savedRows.map((row) => {
                      const invoiceId = row.invoiceId || row.financeInvoiceId;
                      const invoicePath =
                        clientId && invoiceId
                          ? `/commissions/edit-invoice/${clientId}/${invoiceId}`
                          : null;
                      const canEdit = isPendingInstallment(row);
                      const canDelete = canEdit;
                      const isSelected = selectedInstallmentIds.includes(
                        String(row.id),
                      );
                      return (
                        <tr key={row.id} className="border-t border-slate-100">
                          <td className="px-2 py-2 align-middle">
                            {canDelete ? (
                              <input
                                type="checkbox"
                                className="h-3.5 w-3.5 rounded border-slate-300"
                                checked={isSelected}
                                disabled={saving}
                                aria-label={`Select bonus installment ${row.installmentNo}`}
                                onChange={() => toggleInstallmentSelection(row)}
                              />
                            ) : (
                              <span
                                className="inline-block h-3.5 w-3.5"
                                title="Cannot delete"
                              />
                            )}
                          </td>
                          <td className="px-2.5 py-2 font-semibold text-slate-700">
                            {row.installmentNo}
                          </td>
                          <td className="px-2.5 py-2">
                            <span className="inline-flex items-center gap-1 text-slate-700">
                              <FaCalendarAlt
                                size={9}
                                className="text-slate-400"
                              />
                              {row.dueDate}
                            </span>
                          </td>
                          <td className="px-2.5 py-2 tabular-nums font-semibold text-amber-700">
                            {formatCurrency(row.bonusAmount, currency)}
                          </td>
                          <td className="px-2.5 py-2 tabular-nums font-semibold text-emerald-700">
                            {formatCurrency(
                              row.bonusReceived ?? row.bonus_received,
                              currency,
                            )}
                          </td>
                          <td className="px-2.5 py-2">
                            <Chip tone={statusTone(row.status)}>
                              {row.status === "paid" ? "Paid" : row.status}
                            </Chip>
                          </td>
                          <td className="px-2.5 py-2">
                            {invoiceId ? (
                              invoicePath ? (
                                <Link
                                  to={invoicePath}
                                  state={{ returnTo }}
                                  className="text-[11px] font-semibold text-[#007BFF] hover:underline"
                                  style={{ color: "#007BFF" }}
                                >
                                  {row.invoiceNumber || `#${invoiceId}`}
                                </Link>
                              ) : (
                                <span className="text-[11px] font-semibold text-slate-600">
                                  {row.invoiceNumber || `#${invoiceId}`}
                                </span>
                              )
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-2.5 py-2 text-right">
                            {canEdit || canDelete ? (
                              <div className="inline-flex items-center justify-end gap-1">
                                {canEdit ? (
                                  <button
                                    type="button"
                                    title="Edit installment"
                                    aria-label="Edit installment"
                                    onClick={() => setEditRow(row)}
                                    disabled={saving}
                                    className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-60"
                                  >
                                    <FaEdit size={10} />
                                  </button>
                                ) : null}
                                {canDelete ? (
                                  <button
                                    type="button"
                                    title="Delete installment"
                                    aria-label="Delete installment"
                                    onClick={() => handleDeleteInstallment(row)}
                                    disabled={saving}
                                    className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-60"
                                  >
                                    <FaTrash size={10} />
                                  </button>
                                ) : null}
                              </div>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      <CreateBonusInstallmentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        plan={plan}
        bonus={bonus}
        currency={currency}
        onSaved={onSaved}
        mode={modalMode}
        bonusBudget={modalMode === "append" ? remainingBonus : planBonus}
        nextInstallmentNo={modalMode === "append" ? nextInstallmentNo : 1}
      />
      <EditBonusInstallmentModal
        open={Boolean(editRow)}
        row={editRow}
        bonus={bonus}
        currency={currency}
        otherBonusTotal={Math.max(
          0,
          allocatedBonus - (Number(editRow?.bonusAmount) || 0),
        )}
        onClose={() => setEditRow(null)}
        onSaved={onSaved}
      />
    </>
  );
}

export default BonusInstallmentsCard;
